import { isAxiosError } from "axios";

import type { SessionContextChoice } from "@thalamos/haven-common";

import api from "../api/index.js";
import { useUser } from "./UserContext.js";

/**
 * How a move to another context went. `refused` is the server saying no;
 * `failed` is the server not answering or erring, or the user not read again
 * after the move. The recovery differs, so the caller has to render which.
 */
export type SessionContextSwitchOutcome = "accepted" | "refused" | "failed";

export type SessionContextSwitchProblem = Exclude<
  SessionContextSwitchOutcome,
  "accepted"
>;

/**
 * Moves the session to act in another context, and reads the user again so
 * the app sees the move. Where to go afterwards is the caller's: pages are
 * scoped by context, so what is on screen may be nothing to do with it.
 */
export const useSwitchSessionContext = (): ((
  context: SessionContextChoice,
) => Promise<SessionContextSwitchOutcome>) => {
  const { refetchUser } = useUser();

  return async (context) => {
    const response = await api.user
      .setSessionContext(context)
      .catch((error: unknown) => {
        if (isAxiosError(error)) {
          return undefined;
        }
        throw error;
      });

    if (response === undefined) {
      return "failed";
    }

    // 4xx resolves rather than throwing (`validateStatus` in `api/axios/api.ts`);
    // mistaken for success it re-skins the shell for a context nobody holds.
    if (response.status >= 400) {
      return "refused";
    }

    // The mutation has already committed, so a failed refresh is a client out
    // of step rather than a refusal, and moving on would carry it onward.
    try {
      await refetchUser();
    } catch {
      return "failed";
    }

    return "accepted";
  };
};
